import * as React from "react";

import { faBars } from "@performyard/fontawesome/pro-regular-svg-icons";

import type { TopNavProps } from "./types";

import { cn } from "../../../lib/utils";

import { Icon } from "../../atoms/Icon";
import { SIDE_NAV_DOM_ID, buildSideNavThemeStyle } from "../SideNav";

export const TopNav = React.forwardRef<HTMLElement, TopNavProps>((props, ref) => {
	const {
		logo,
		theme,
		searchSlot,
		actionsSlot,
		userMenu,
		onToggleMobileSidenav,
		isMobileSidenavOpen = false,
		dangerouslySetClassName,
		"data-testid": dataTestId = "top-nav",
	} = props;

	const themeStyle = React.useMemo(() => buildSideNavThemeStyle(theme), [theme]);
	const hasLogo = Boolean(logo);
	const themeVars = React.useMemo(() => {
		// Only a bar that paints itself with the side-nav colors should push its foreground onto
		// slotted actions. On the default white bar `--sidenav-fg` is the *side nav's* colour, so
		// letting actions read it directly renders them white-on-white.
		const actionColor = hasLogo ? { "--pyo-topnav-action-color": "var(--sidenav-fg)" } : undefined;
		if (!themeStyle) return actionColor as React.CSSProperties | undefined;
		const vars = { ...themeStyle };
		delete vars.backgroundColor;
		delete vars.color;
		return { ...vars, ...actionColor } as React.CSSProperties;
	}, [themeStyle, hasLogo]);

	return (
		<header
			ref={ref}
			className={cn(
				"box-border flex h-full w-full items-center border-b border-solid border-[color:rgba(47,50,55,0.2)] pr-8 pl-0 max-md:pr-3",
				!hasLogo && "bg-neutral-0",
				hasLogo && "bg-[color:var(--sidenav-bg)] text-[color:var(--sidenav-fg)]",
				dangerouslySetClassName
			)}
			style={themeVars}
			data-testid={dataTestId}
		>
			{!hasLogo && onToggleMobileSidenav ? (
				<button
					type="button"
					onClick={onToggleMobileSidenav}
					aria-label={isMobileSidenavOpen ? "Close navigation" : "Open navigation"}
					aria-expanded={isMobileSidenavOpen}
					aria-controls={SIDE_NAV_DOM_ID}
					data-testid="topnav-mobile-hamburger"
					className="ml-2 inline-flex size-9 shrink-0 cursor-pointer appearance-none items-center justify-center self-center rounded-md border-0 bg-transparent text-neutral-9 hover:bg-neutral-1 focus-visible:ring-2 focus-visible:ring-neutral-9 focus-visible:outline-hidden md:hidden"
				>
					<Icon icon={faBars} size={20} color={null} />
				</button>
			) : null}
			{hasLogo ? (
				<div
					data-testid="topnav-logo-column"
					className="box-border flex h-full w-[287px] shrink-0 items-center justify-center overflow-hidden max-md:justify-start max-md:pl-3"
				>
					{logo}
				</div>
			) : null}
			<div
				className={cn(
					"box-border flex h-full flex-1 items-center justify-end gap-4 max-md:gap-2",
					hasLogo && "ml-6"
				)}
			>
				{searchSlot ? <div className="hidden items-center sm:flex">{searchSlot}</div> : null}
				{/* empty:hidden — a self-gating slot component is still a truthy element, so without it a
				    slot that renders null leaves a zero-width div and the parent's gap on both sides of it. */}
				{actionsSlot ? <div className="flex items-center empty:hidden">{actionsSlot}</div> : null}
				{userMenu}
			</div>
		</header>
	);
});

TopNav.displayName = "TopNav";

export type { TopNavProps } from "./types";
